import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { differenceInMonths } from 'date-fns';
import _findIndex from 'lodash/findIndex';
import _isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';

import styles from './styles.module.scss';

import { generateMonthsData, getDefaultMonth } from './helpers';
import { days, defaultDateRange, monthNames } from './constants';
import Button from '@components/_shared/button';
import { useIsMobile } from '@utils/hooks/useIsMobile';
import { BaseComponentProps } from '@utils/types/baseComponents';
import {
  CalendarMonth as CalendarMonthType,
  DatesRange,
} from '@utils/types/calendar';
import { ArrowRight, ChevronLeft, ChevronRight } from '@assets/icons';
import { SelectionWithSuggestions } from './selectionWithSuggestions';
import { EstimateUpsellAlternativeType } from '@utils/types/estimate';
import { parseDateWithoutTimezone } from '@utils/dates';
import { ListingMonth } from './listingMonth';
import { colors } from '@utils/constants/colors';
import { useHandlePickerKeyboardEvents } from '@components/_shared/datePickers/useHandlePickerKeyboardEvents';

type Props = {
  onDatesChange: (dates: DatesRange) => void;
  toggleCalendar?: () => void;
  value: DatesRange;
  disabledDates: string[];
  nightlyPrices: Record<string, Record<string, number[]>>;

  withUpsellSuggestions: boolean;
  upsellAlternative?: EstimateUpsellAlternativeType;
  setUpsellAlternative?: (value?: EstimateUpsellAlternativeType) => void;
  listingId?: string;

  withPetFee?: boolean;

  onApply?: () => void;
  onClearDates?: () => void;
  minNightsStay?: number;
  maxNightsStay?: number;
  firstAvailableDate?: string | null;
  lastKnownDay?: string | null;

  /**
   * If true, the calendar will be in the mode of selecting the new end date
   * to extend the reservation
   */
  selectNewEndDateMode?: boolean;
} & BaseComponentProps;

export function ListingPicker(props: Props) {
  const {
    containerStyle,
    listingId,
    onDatesChange,
    toggleCalendar,
    onApply,
    value,
    disabledDates,
    onClearDates,
    minNightsStay,
    maxNightsStay,
    firstAvailableDate,
    lastKnownDay,
    nightlyPrices,
    upsellAlternative,
    setUpsellAlternative,
    withPetFee = false,
    withUpsellSuggestions,
    selectNewEndDateMode,
  } = props;

  const months = useMemo(
    () =>
      generateMonthsData(
        lastKnownDay
          ? differenceInMonths(new Date(lastKnownDay), new Date()) + 1
          : 12
      ),
    []
  );

  const [selectedDates, setSelectedDates] =
    useState<DatesRange>(defaultDateRange);
  const [currentMonth, setCurrentMonth] = useState<CalendarMonthType>(
    getDefaultMonth(value, months, firstAvailableDate) || months[0]
  );
  const [selectedUpsellAlternative, setSelectedUpsellAlternative] = useState<
    EstimateUpsellAlternativeType | undefined
  >(upsellAlternative);

  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();

  const { currentMonthIndex, nextMonthIndex } = useMemo(() => {
    const currentMonthIndex = _findIndex(
      months,
      (v) =>
        _isEqual(v.month, currentMonth.month) &&
        _isEqual(v.year, currentMonth.year)
    );
    const nextMonthIndex = Math.min(currentMonthIndex + 1, months.length - 1);
    return { currentMonthIndex, nextMonthIndex };
  }, [currentMonth, months]);

  const nextMonth = useMemo(
    () => months[nextMonthIndex],
    [months, nextMonthIndex]
  );

  const clearDates = () => {
    setSelectedDates(defaultDateRange);
    onDatesChange(defaultDateRange);
    if (onClearDates) {
      onClearDates();
    }
  };

  const selectDate = (date: Date) => {
    setSelectedUpsellAlternative(undefined);

    let newDates: DatesRange;
    if (!!selectedDates.from) {
      if (!!selectedDates.to) {
        newDates = {
          from: date,
          to: null,
        };
      } else {
        newDates = {
          from: selectedDates.from,
          to: date,
        };
      }
    } else {
      newDates = {
        from: date,
        to: null,
      };
    }

    if (newDates.from && newDates.to) {
      if (newDates.from.getTime() > newDates.to.getTime()) {
        newDates = {
          from: newDates.to,
          to: newDates.from,
        };
      }
    }

    setSelectedDates(newDates);
    onDatesChange(newDates);
  };

  const changeDates = (dates: DatesRange) => {
    setSelectedDates(dates);
    onDatesChange(dates);
  };

  const goToPrevMonth = useCallback(() => {
    if (currentMonthIndex === 0) {
      return;
    }
    setCurrentMonth(months[currentMonthIndex - 1]);
  }, [currentMonthIndex, months]);

  const goToNextMonth = useCallback(() => {
    if (nextMonthIndex === months.length - 1) {
      return;
    }
    setCurrentMonth(months[nextMonthIndex]);
  }, [nextMonthIndex, months]);

  const goToMonthByMonthIndex = useCallback(
    (monthIndex: number) => {
      if (monthIndex < 0 || monthIndex >= months.length) {
        return;
      }
      if (
        _isEqual(currentMonth.month, months[monthIndex].month) &&
        _isEqual(currentMonth.year, months[monthIndex].year)
      ) {
        return;
      }
      setCurrentMonth(months[monthIndex]);
    },
    [currentMonth.month, months]
  );

  useEffect(() => {
    /**
     * On desktop, we show 2 months at a time,
     * on mobile - only one
     */
    const offset = isMobile ? 0 : 1;

    if (selectedUpsellAlternative) {
      const upsellCheckoutMonth = parseDateWithoutTimezone(
        selectedUpsellAlternative.end
      ).getMonth();
      const upsellCheckoutYear = parseDateWithoutTimezone(
        selectedUpsellAlternative.end
      ).getFullYear();

      if (
        (months[currentMonthIndex].month !== upsellCheckoutMonth ||
          months[currentMonthIndex].year !== upsellCheckoutYear) &&
        (months[currentMonthIndex + offset].month !== upsellCheckoutMonth ||
          months[currentMonthIndex + offset].year !== upsellCheckoutYear)
      ) {
        goToMonthByMonthIndex(
          months.findIndex(
            (m) =>
              _isEqual(m.month, upsellCheckoutMonth) &&
              _isEqual(m.year, upsellCheckoutYear)
          ) - offset
        );
      }
    } else {
      if (
        selectedDates.from &&
        selectedDates.to &&
        (months[currentMonthIndex + offset].month !==
          selectedDates.to.getMonth() ||
          months[currentMonthIndex + offset].year !==
            selectedDates.to.getFullYear())
      ) {
        goToMonthByMonthIndex(
          months.findIndex(
            (m) =>
              _isEqual(m.month, selectedDates.to?.getMonth()) &&
              _isEqual(m.year, selectedDates.to?.getFullYear())
          ) - offset
        );
      } else {
        /**
         *  Scroll to the specific month if some date range
         *  is already selected when the didMount event occurs
         */
        goToMonthByMonthIndex(
          months.findIndex(
            (m) =>
              _isEqual(m.month, currentMonth.month) &&
              _isEqual(m.year, currentMonth.year)
          )
        );
      }
    }
  }, [selectedUpsellAlternative?.end]);

  useHandlePickerKeyboardEvents({
    ref: containerRef,
    goToNextMonth,
    goToPrevMonth,
  });

  useEffect(() => {
    setSelectedDates(value);
  }, [value]);

  const onApplyClick = () => {
    if (setUpsellAlternative) {
      setUpsellAlternative(selectedUpsellAlternative);
    }
    if (onApply) {
      onApply();
    }
  };

  return (
    <div
      className={clsx([styles.listingPicker, containerStyle])}
      ref={containerRef}
    >
      <div className={styles.header}>
        <div onClick={goToPrevMonth} className={styles.header__arrow}>
          <ChevronLeft />
        </div>
        <div onClick={goToNextMonth} className={styles.header__arrow}>
          <ChevronRight />
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.monthWrapper}>
          <div className={styles.monthName}>
            {monthNames[currentMonth.month]} {currentMonth.year}
          </div>

          <div className={styles.week}>
            {days.map((day, idx) => (
              <div key={idx} className={styles.day}>
                {day}
              </div>
            ))}
          </div>

          <div className={styles.list} style={{ height: 'max-content' }}>
            <ListingMonth
              month={currentMonth}
              selectedDates={selectedDates}
              disabledDates={disabledDates}
              onDateSelect={selectDate}
              minNightsStay={minNightsStay}
              maxNightsStay={maxNightsStay}
              lastKnownDay={lastKnownDay}
              selectNewEndDateMode={selectNewEndDateMode}
            />
          </div>
        </div>

        {!isMobile && (
          <div className={styles.monthWrapper}>
            <div className={styles.monthName}>
              {monthNames[nextMonth.month]} {nextMonth.year}
            </div>

            <div className={styles.week}>
              {days.map((day, idx) => (
                <div key={idx} className={styles.day}>
                  {day}
                </div>
              ))}
            </div>

            <div className={styles.list} style={{ height: 'max-content' }}>
              <ListingMonth
                month={nextMonth}
                selectedDates={selectedDates}
                disabledDates={disabledDates}
                onDateSelect={selectDate}
                minNightsStay={minNightsStay}
                maxNightsStay={maxNightsStay}
                lastKnownDay={lastKnownDay}
                selectNewEndDateMode={selectNewEndDateMode}
              />
            </div>
          </div>
        )}
      </div>

      {minNightsStay && minNightsStay > 1 && (
        <div className={styles.minimumStayWarning}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
          >
            <path
              d="M8.875 11.5C8.875 11.5995 8.83549 11.6948 8.76517 11.7652C8.69484 11.8355 8.59946 11.875 8.5 11.875C8.26794 11.875 8.04538 11.7828 7.88128 11.6187C7.71719 11.4546 7.625 11.2321 7.625 11V8.5C7.625 8.46685 7.61183 8.43505 7.58839 8.41161C7.56495 8.38817 7.53315 8.375 7.5 8.375C7.40055 8.375 7.30516 8.33549 7.23484 8.26516C7.16451 8.19484 7.125 8.09946 7.125 8C7.125 7.90054 7.16451 7.80516 7.23484 7.73484C7.30516 7.66451 7.40055 7.625 7.5 7.625C7.73207 7.625 7.95463 7.71719 8.11872 7.88128C8.28282 8.04538 8.375 8.26794 8.375 8.5V11C8.375 11.0332 8.38817 11.0649 8.41161 11.0884C8.43506 11.1118 8.46685 11.125 8.5 11.125C8.59946 11.125 8.69484 11.1645 8.76517 11.2348C8.83549 11.3052 8.875 11.4005 8.875 11.5ZM7.75 6.375C7.87362 6.375 7.99445 6.33834 8.09723 6.26967C8.20002 6.20099 8.28012 6.10338 8.32743 5.98918C8.37473 5.87497 8.38711 5.74931 8.36299 5.62807C8.33888 5.50683 8.27935 5.39547 8.19195 5.30806C8.10454 5.22065 7.99317 5.16112 7.87193 5.13701C7.7507 5.11289 7.62503 5.12527 7.51083 5.17258C7.39662 5.21988 7.29901 5.29999 7.23033 5.40277C7.16166 5.50555 7.125 5.62639 7.125 5.75C7.125 5.91576 7.19085 6.07473 7.30806 6.19194C7.42527 6.30915 7.58424 6.375 7.75 6.375ZM14.375 8.5C14.375 9.76086 14.0011 10.9934 13.3006 12.0418C12.6001 13.0901 11.6045 13.9072 10.4396 14.3897C9.27473 14.8722 7.99293 14.9985 6.7563 14.7525C5.51967 14.5065 4.38376 13.8994 3.4922 13.0078C2.60064 12.1162 1.99348 10.9803 1.7475 9.7437C1.50152 8.50707 1.62776 7.22527 2.11027 6.06039C2.59278 4.89551 3.40988 3.89988 4.45824 3.19938C5.50661 2.49889 6.73915 2.125 8 2.125C9.69015 2.12698 11.3105 2.79927 12.5056 3.99439C13.7007 5.1895 14.373 6.80985 14.375 8.5ZM13.625 8.5C13.625 7.38748 13.2951 6.29994 12.677 5.37492C12.0589 4.44989 11.1804 3.72892 10.1526 3.30318C9.12476 2.87743 7.99376 2.76604 6.90262 2.98308C5.81148 3.20012 4.8092 3.73585 4.02253 4.52252C3.23586 5.30919 2.70013 6.31147 2.48309 7.40262C2.26604 8.49376 2.37744 9.62476 2.80318 10.6526C3.22892 11.6804 3.94989 12.5589 4.87492 13.177C5.79995 13.7951 6.88748 14.125 8 14.125C9.49134 14.1233 10.9211 13.5302 11.9757 12.4756C13.0302 11.4211 13.6233 9.99133 13.625 8.5Z"
              fill="#8F8B83"
            />
          </svg>

          <span>This listing requires a {minNightsStay}-night minimum</span>
        </div>
      )}

      <div className={styles.calendarLegend}>
        <div className={styles.calendarLegend__item}>
          <div className={styles.calendarLegend__valid} />
          <div className={styles.calendarLegend__label}>
            Valid{' '}
            {selectedDates.from && (!selectedDates.to || selectNewEndDateMode)
              ? 'end'
              : 'start'}{' '}
            date
          </div>
        </div>

        <div className={styles.calendarLegend__item}>
          <div className={styles.calendarLegend__available} />
          <div className={styles.calendarLegend__label}>Available</div>
        </div>

        <div className={styles.calendarLegend__item}>
          <div className={styles.calendarLegend__taken} />
          <div className={styles.calendarLegend__label}>Taken</div>
        </div>

        {selectNewEndDateMode ? (
          <div className={styles.calendarLegend__item}>
            <div className={styles.calendarLegend__oldSelected} />
            <div className={styles.calendarLegend__label}>Your reservation</div>
          </div>
        ) : (
          <div className={styles.calendarLegend__item}>
            <div className={styles.calendarLegend__selected} />
            <div className={styles.calendarLegend__label}>Selected</div>
          </div>
        )}
      </div>

      {withUpsellSuggestions &&
        selectedDates.from &&
        selectedDates.to &&
        listingId && (
          <SelectionWithSuggestions
            listingId={listingId}
            selectedDates={selectedDates}
            selectedUpsellAlternative={selectedUpsellAlternative}
            changeUpsellAlternative={setSelectedUpsellAlternative}
            changeDates={changeDates}
            withPetFee={withPetFee}
            isInstantBooking={minNightsStay ? minNightsStay < 30 : false}
          />
        )}

      {!!onApply && (
        <div className={styles.listingPickerFooter}>
          <div>
            <div className={styles.listingPickerFooter__text}>
              Don’t see your dates available?
            </div>

            <div
              className={styles.listingPickerFooter__contactButton}
              role="button"
              // @ts-ignore
              onClick={() => window?.FrontChat('show')}
              aria-label="Contact our reservations team"
            >
              Contact our reservations team
              <ArrowRight stroke={colors.roveLightBlue} />
            </div>
          </div>

          <div className={styles.listingPickerFooter__buttons}>
            <Button
              onClick={clearDates}
              containerStyle={styles.listingPickerFooter__clearButton}
              variant="offWhiteContained"
            >
              Clear Dates
            </Button>

            <Button
              onClick={onApplyClick}
              disabled={!selectedDates.to || !selectedDates.from}
              containerStyle={styles.listingPickerFooter__actionButton}
              variant="greenContained"
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
